import React, { useState } from 'react'

import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined'
import { Button, IconButton, Tooltip } from '@mui/material'
import { useCustomerRevealApiExtraQuotaSet } from 'features/customers/hooks/useCustomerRevealApiExtraQuotaSet'
import { useCustomerRevealApiQuota } from 'features/customers/hooks/useCustomerRevealApiQuota'

import { hasRevealConfig, revealCredits, revealMonthlyLimit } from '@albacross/types'

import CustomerRevealApiExtraQuotaModal from './CustomerRevealApiExtraQuotaModal'

// compact: a row action in the Features table, where a primary button competes with the data.
type CustomerRevealApiExtraQuotaButtonProps = {
  compact?: boolean
  type: 'identified' | 'requests' | 'uniqueIdentifiedIps'
}

const CustomerRevealApiExtraQuotaButton = ({ type, compact }: CustomerRevealApiExtraQuotaButtonProps) => {
  const [open, setOpen] = useState(false)
  const { data = {} } = useCustomerRevealApiQuota()
  const { mutateAsync: setExtraQuota, isPending: isLoading } = useCustomerRevealApiExtraQuotaSet()

  // A customer without a Reveal API user has no quota fields; Reveal rejects extra-quota writes for them.
  const hasRevealApi = hasRevealConfig(data)

  const handleConfirm = async (value: number) => {
    try {
      await setExtraQuota({ type, value })
      setOpen(false)
    } catch {
      // error is surfaced by the mutation's onError notification; keep the modal open
    }
  }

  const button = compact ? (
    <IconButton aria-label="Set temporary credits" disabled={!hasRevealApi} onClick={() => setOpen(true)} size="small">
      <AddCircleOutlinedIcon fontSize="small" />
    </IconButton>
  ) : (
    <Button color="primary" disabled={!hasRevealApi} onClick={() => setOpen(true)}>
      Set temporary credits
    </Button>
  )

  return (
    <>
      {/* A disabled control swallows its own hover, so the tooltip goes on a wrapper either way. */}
      <Tooltip title={hasRevealApi ? 'Set temporary credits' : 'Customer has no Reveal API'}>
        <span>{button}</span>
      </Tooltip>
      {open && (
        <CustomerRevealApiExtraQuotaModal
          currentCredits={revealCredits(data, type)}
          disabled={isLoading}
          monthlyLimit={revealMonthlyLimit(data, type)}
          onClose={() => setOpen(false)}
          onConfirm={handleConfirm}
          open={open}
        />
      )}
    </>
  )
}

export default CustomerRevealApiExtraQuotaButton
